/**
 * Search filters component with pill-button UI.
 *
 * Layout:
 * 1. Document Type: Labels | FDA Approval Letters | FDA Enforcement Letters |
 *    Label Negotiations   (then Competitive Intel and
 *    Multi-Disciplinary Review; order comes from SURFACE_DOC_TYPES)
 * 2. Region: US | EU | UK | CA | AU (multi-select pills)              [drug-only]
 * 3. Application Type (US only): NDA | BLA | NDA Authorized Generic    [drug-only]
 * 4. Additional Filters (US only, expandable): OTC | Homeopathic | …   [drug-only]
 *
 * When ``corpus === 'fda_letters'`` or ``'ci'`` the drug-only rows (Region,
 * US/non-US categories, Additional Filters) are hidden.  When ``corpus ===
 * 'approval_letters'`` only Region is hidden — that corpus is US-only and
 * region-free, but it still scopes by marketing category server-side.
 * The Document Type pill row always stays visible so the user can switch
 * back to Labels.
 */
import React, { useState } from 'react';
import type { Corpus, SearchFilterOptions, SearchModeValue, SearchRegion } from '../types';
import {
  visibleRegionOptions,
  toggleRegion,
  toggleRegionCogni,
  isRegionDisabled,
  getDisabledRegionTooltip,
  isKeywordRegionDisabled,
  keywordRegionOptions,
  LETTER_TYPE_OPTIONS,
  LETTER_CORPUS_OPTIONS,
  LETTER_ROLE_OPTIONS,
  LETTER_DIVISION_OPTIONS,
  surfaceModes,
  isModeEnabled,
  MODE_DISABLED_TOOLTIP,
  NO_LABEL_NEGOTIATIONS_TOOLTIP,
  INSTANT_LABELS_ONLY_TOOLTIP,
  SBA_DOC_TYPE_OPTIONS,
  normalizeSbaDocTypes,
  toggleSbaDocType,
  isUsReviewScope,
  isUsFdaVisible,
  regionDisplayLabel,
} from '../constants/filterConstants';
import type { Surface } from '../constants/filterConstants';
import { coerceSearchMode } from '../utils/coerceSearchMode';
import Tooltip from './Tooltip';
import { useIsAdmin } from '../hooks/useIsAdmin';
import { useHasLabelNegotiations } from '../hooks/useHasLabelNegotiations';

interface SearchFiltersProps {
  /** Page-level corpus toggle.  When ``'fda_letters'`` or ``'ci'`` the
   *  drug-only filter sub-rows (Region, US/Non-US application categories,
   *  Additional Filters) are hidden — neither corpus has a region or
   *  marketing-category, so showing the controls would be misleading.
   *  ``'approval_letters'`` hides Region only: it is US-only and region-free,
   *  but marketing category still scopes the search.
   *  The Document Type pill row stays visible so the user can switch
   *  back to Labels.  Default ``'labels'`` keeps the existing UX. */
  corpus?: Corpus;
  filters: SearchFilterOptions;
  onFiltersChange: (filters: SearchFilterOptions) => void;
  isLoading?: boolean;
  /** Extra fields rendered before Document Type (e.g. Drug/Mfr inputs for keyword search) */
  children?: React.ReactNode;
  /** Compact single-line mode for results page top bar */
  compact?: boolean;
  /** Keyword-search context: gate region pills to US-only (EU/UK/CA/AU greyed
   *  "Coming soon"). Cogni/Investigator omit this and use DISABLED_REGIONS. */
  keywordUsOnly?: boolean;
  /** Which surface is rendering these filters — drives per-surface doc-type
   *  greyout.  Defaults to 'cogni' to preserve the existing Cogni behaviour
   *  for callers that haven't been updated yet. */
  surface?: Surface;
  /** When provided (authenticated Cogni), renders the "Include draft label
   *  negotiations" checkbox in the Document Type section (labels mode only). */
  includeLabelNegotiations?: boolean;
  onIncludeLabelNegotiationsChange?: (v: boolean) => void;
  /** Greys every Document Type pill except Labels (Keyword surface's Instant
   *  mode, which only searches labels). */
  labelsOnly?: boolean;
  /** Keyword surface: one flat row of labelled groups (no card), with
   *  not-yet-clickable regions rendered as "· soon" chips. */
  inline?: boolean;
  /** Inline layout only — admin "Search in" multi-select (spec 2026-10-16
   *  §6a). When given it REPLACES the Document Type pill group. */
  documentsSlot?: React.ReactNode;
  /** Inline layout only — show the enforcement-letter filter pills
   *  regardless of ``corpus`` (the admin multi-select keeps corpus 'labels'
   *  while Enforcement Letters is selected). Default: ``corpus ===
   *  'fda_letters'``. */
  showLetterFilters?: boolean;
}

// ── Pill button helper ─────────────────────────────────────────────────
function Pill({
  label,
  active,
  onClick,
  disabled,
  small,
  title,
  mono,
  soon,
}: {
  label: React.ReactNode;
  active: boolean;
  onClick: () => void;
  disabled?: boolean;
  small?: boolean;
  title?: string | undefined;
  mono?: boolean;
  /** Not clickable for this user yet: dashed chip reading "<label> · soon". */
  soon?: boolean;
}) {
  const state = soon
    ? 'border-dashed border-line-input bg-transparent text-ink-3 cursor-not-allowed'
    : `${
        active
          ? 'border-accent bg-accent-tint text-accent-deep font-medium'
          : 'border-line-input bg-white text-ink hover:border-ink-3'
      } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`;
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      aria-pressed={active}
      {...(soon ? { 'aria-disabled': true } : {})}
      className={`inline-flex items-center rounded-ctl border transition-colors duration-150 ${
        small ? 'h-7 px-2 text-xs' : 'h-[30px] px-2.5 text-[13px]'
      } ${mono ? 'font-mono text-[12px] px-[9px]' : ''} ${state}`}
    >
      {soon ? <>{label} · soon</> : label}
    </button>
  );
}

// ── Letter-filter pill row helper ──────────────────────────────────────
// Renders one labelled row of pill buttons (Letter Type / Letter Corpus /
// Type-role).  Used in both the full and compact ``SearchFilters`` layouts.
// Module-scope so it isn't reallocated on every parent render.  Options
// come from the ``LETTER_*_OPTIONS`` ``as const`` tuples in
// ``filterConstants``; the ``small`` flag mirrors the existing ``Pill``
// size variants so the same helper renders for both layouts.
function LetterPillGroup({
  label,
  options,
  selected,
  onToggle,
  isLoading,
  small,
}: {
  label: string;
  options: readonly { key: string; label: string; tooltip?: string }[];
  selected: string[];
  onToggle: (key: string) => void;
  isLoading: boolean;
  small: boolean;
}) {
  return (
    <div className={small ? 'flex items-center gap-2' : ''}>
      <span
        className={
          small
            ? 'text-xs font-semibold text-slate-500'
            : 'text-sm font-semibold text-slate-700 block mb-2'
        }
      >
        {label}
      </span>
      <div className="flex items-center gap-2 flex-wrap">
        {options.map((opt) => {
          const pill = (
            <Pill
              key={opt.key}
              label={opt.label}
              active={selected.includes(opt.key)}
              onClick={() => onToggle(opt.key)}
              disabled={isLoading}
              small={small}
            />
          );
          return opt.tooltip
            ? <Tooltip key={opt.key} text={opt.tooltip}>{pill}</Tooltip>
            : pill;
        })}
      </div>
    </div>
  );
}

// ── Mode options ───────────────────────────────────────────────────────
// ``draft_review`` (Label Negotiations) is greyed for non-admins until the
// UX work ships; admins get to click it so they can dogfood the feature.
// ``multi_review`` (HA Reviews / SBA) is OPEN TO ALL as of 2026-09-01 —
// rendered and clickable for every authenticated user, greyed only when the
// REGION is wrong (Cogni US; Investigator US/EU/JP). It no longer follows the
// draft_review shape above, so do not "simplify" the two back together.
// The base list is rendered for everyone; ``buildModeOptions(isAdmin)`` is
// the source of truth for what each user sees.
type ModeOption = {
  value: SearchModeValue;
  label: React.ReactNode;
  disabled: boolean;
  /** Overrides the shared "Coming soon ..." hover text at the render sites. */
  tooltip?: string;
};

// MDR_COMING_SOON_TOOLTIP was deleted 2026-09-01 along with the HA Reviews
// admin gate: with the pill usable by everyone, its only remaining greyout is
// the region rule, which the shared MODE_DISABLED_TOOLTIP ("Coming soon ...")
// already covers. Its old comment claimed it was shared with other greyed
// pills — it was not; nothing else ever referenced it.
//
// A Label Negotiations pill with no tenant drafts is now the ONLY tooltip
// override (NO_LABEL_NEGOTIATIONS_TOOLTIP below).

// Small inline badge used inside Document Type pills to mark
// not-yet-GA features. Renders as a tiny uppercase chip after the
// label text. Kept inline (not a separate component) because it's
// only used here and reads better at the call-site.
const BetaBadge = () => (
  <span className="ml-1.5 px-1 py-px text-[9px] font-bold uppercase tracking-wide rounded bg-amber-100 text-amber-800 align-middle">
    Beta
  </span>
);

/**
 * Plain-text label for a search mode (shared by the mode pills and the Cogni
 * scope chips). The mode VALUE `multi_review` is in deep-links and
 * ?researchSearchMode, so only its visible label ("HA Reviews") is renamed.
 */
export function modeLabelText(mode: SearchModeValue): string {
  if (mode === 'fda_letters') return 'FDA Enforcement Letters';
  if (mode === 'labels') return 'Labels';
  if (mode === 'draft_review') return 'Label Negotiations';
  if (mode === 'label_negotiations') return 'Label Negotiations';
  if (mode === 'multi_review') return 'HA Reviews';
  if (mode === 'ci') return 'Competitive Intel';
  if (mode === 'approval_letters') return 'FDA Approval Letters';
  return mode;
}

/**
 * Build the mode option list for a given surface/admin/region context.
 *
 * Exported so tests can call it as a pure function without rendering
 * the full component (no React context, no useIsAdmin required).
 *
 * - ``surface``: which search surface is rendering ('cogni'|'investigator'|'keyword')
 * - ``isAdmin``: drives which modes are rendered at all
 * - ``regions``: drives per-mode enabled state (e.g. Approval Letters → US-only,
 *   Cogni multi_review → US-only, Investigator multi_review → US/EU/JP)
 *
 * Investigator renders multi_review but never draft_review — it uses
 * label_negotiations for the same visible "Label Negotiations" label.
 * multi_review is rendered and enabled for EVERYONE on both surfaces,
 * subject to the per-region rules in isModeEnabled (filterConstants).
 */
export function buildModeOptions(
  isAdmin: boolean,
  surface: Surface = 'cogni',
  regions: string[] = ['US'],
  hasLabelNegotiations: boolean = true,
): ModeOption[] {
  // Labels for each mode value — React nodes so BetaBadge can be inlined.
  const labelFor = (mode: SearchModeValue): React.ReactNode => {
    // HA Reviews carries the Beta badge while its reranker quality is still
    // being tuned; every other label is the plain text from modeLabelText.
    if (mode === 'multi_review') return <><span>{modeLabelText(mode)}</span><BetaBadge /></>;
    return modeLabelText(mode);
  };

  return surfaceModes(surface, isAdmin).map((mode) => {
    const disabled = !isModeEnabled(mode, surface, regions, isAdmin, hasLabelNegotiations);
    // Is the tenant-data flag the SOLE reason this pill is greyed? Re-ask with
    // the flag forced on: if it would be enabled, the missing drafts are what
    // blocked it. Anything else (region, admin, feature flag) keeps the
    // default copy — a US-only pill greyed on EU is not a data problem.
    const blockedOnlyByMissingDrafts =
      disabled && !hasLabelNegotiations && isModeEnabled(mode, surface, regions, isAdmin, true);
    // MDR ("HA Reviews") no longer carries "coming soon" for anyone.
    //
    // Until 2026-09-01 the condition here was ``mode === 'multi_review' &&
    // !isAdmin``, because a non-admin could never use the pill. Now they can:
    // the admin gates in ``surfaceModes`` / ``isModeEnabled`` are gone, so the
    // ONLY reason this pill is ever greyed is the region (Cogni is US-only;
    // Investigator adds EU/JP) — for admins and non-admins alike. "Coming
    // soon" would be a lie in both cases: nothing is coming, the region is
    // just wrong. That is exactly what the Approval Letters pill greyed beside
    // it says with the shorter copy, so leaving this branch in would have made
    // two pills disagree about the same situation.
    const tooltip =
      blockedOnlyByMissingDrafts ? NO_LABEL_NEGOTIATIONS_TOOLTIP : undefined;
    return {
      value: mode,
      label: labelFor(mode),
      disabled,
      // Conditional spread rather than `tooltip: undefined` — tsconfig sets
      // exactOptionalPropertyTypes, which rejects an explicit undefined.
      ...(tooltip ? { tooltip } : {}),
    };
  });
}

// ── US main categories (always visible when US selected) ───────────────
const US_MAIN_CATEGORIES = [
  { key: 'nda', label: 'NDA' },
  { key: 'bla', label: 'BLA' },
  { key: 'nda_ag', label: 'Auth. generic' },
];

// ── US additional categories (expandable) ──────────────────────────────
const US_EXTRA_CATEGORIES = [
  { key: 'anda', label: 'ANDA' },
  { key: 'otc', label: 'OTC' },
  { key: 'homeopathic', label: 'Homeopathic' },
  { key: 'animal', label: 'Animal' },
  { key: 'other', label: 'Unapproved' },
];

// ── Non-US region categories ───────────────────────────────────────────
// Only EU + UK expose a marketing-category pill, because only those two have
// a marketing_status that is GENUINELY read from the regulator's source at
// ingestion (EMA medicine_status / MHRA is_discontinued) — both NOT NULL,
// enforced by the chk_marketing_status_genuine DB constraint.
//
// CA/AU/JP are intentionally absent: CA/AU's status was a hardcoded literal
// ("Marketed") and JP's is never set (PMDA has no NDA/BLA-style category), so
// surfacing a pill there would either filter on a fabricated value (CA/AU) or
// match zero rows (JP 'Authorised' → all-NULL → "No results"). Removed
// 2026-06-24; selecting CA/AU/JP now applies no marketing-category filter.
const NON_US_CATEGORIES: Partial<Record<SearchRegion, { key: string; label: string }[]>> = {
  EU: [{ key: 'eu_authorised', label: 'EU Authorised' }],
  UK: [{ key: 'uk_authorised', label: 'UK Authorised' }],
  // US-Drugs@FDA: its own application categories (subset of the US family).
  US_FDA: [
    { key: 'nda', label: 'NDA' },
    { key: 'bla', label: 'BLA' },
    { key: 'anda', label: 'ANDA' },
  ],
};

const SearchFiltersComponent: React.FC<SearchFiltersProps> = ({
  corpus = 'labels',
  filters,
  onFiltersChange,
  isLoading = false,
  children,
  compact = false,
  keywordUsOnly = false,
  surface = 'cogni',
  includeLabelNegotiations,
  onIncludeLabelNegotiationsChange,
  labelsOnly = false,
  inline = false,
  documentsSlot,
  showLetterFilters,
}) => {
  const [showExtra, setShowExtra] = useState(false);
  // Region gating. Two independent sets, by surface: the keyword surface uses
  // KEYWORD_DISABLED_REGIONS, everything else the shared DISABLED_REGIONS.
  // Returns [disabled, tooltip]. The admin bypass is per-surface too
  // (KEYWORD_ADMIN_BYPASSABLE_REGIONS vs ADMIN_BYPASSABLE_REGIONS); both are
  // currently empty, so a greyed pill is greyed for admins as well.
  const regionGate = (region: SearchRegion): [boolean, string] =>
    keywordUsOnly
      ? // Keyword greyout is its own set (KEYWORD_DISABLED_REGIONS: CN/CH/DE),
        // with KEYWORD_ADMIN_BYPASSABLE_REGIONS as the admin door — currently
        // EMPTY, so none of those three is clickable by anyone yet.
        // UK was removed from the keyword greyout 2026-10-27 and is now open to
        // ALL users: it rides the FTS/sidecar path, not the hanging vector step.
        // Its SmPC parse quality is a known, accepted trade.
        // Tooltip comes from the shared per-region map so keyword matches the
        // other surfaces.
        [isKeywordRegionDisabled(region, isAdmin), getDisabledRegionTooltip(region)]
      : [isRegionDisabled(region, isAdmin), getDisabledRegionTooltip(region)];
  // UK cogni + research stay hard-disabled for everyone incl. admins (the
  // monolithic UK vector step hangs the worker) — see ADMIN_BYPASSABLE_REGIONS
  // (empty) in filterConstants. UK KEYWORD is fully open as of 2026-10-27.
  const isAdmin = useIsAdmin();
  const usFdaVisible = isUsFdaVisible(isAdmin);
  // Tenant owns ≥1 draft? Gates all three Label Negotiations controls below
  // (the draft_review pill on Cogni, label_negotiations on Investigator, and
  // the "Include draft label negotiations" checkbox).
  const hasLabelNegotiations = useHasLabelNegotiations();

  // When the page-level corpus is FDA Letters, the drug-only sub-rows
  // (Region, US/Non-US application categories, Additional Filters) are
  // hidden inline below — letters aren't filtered by region or marketing
  // category, so showing those controls would mislead.  The Document
  // Type pill row stays visible so the user can switch back to Labels.
  const isLetters = corpus === 'fda_letters';
  // Region + Application-Type / category rows hide when the corpus is FDA
  // Letters OR Competitive Intel: neither applies a region / marketing-
  // category filter, so the controls would mislead. Worse for ci
  // specifically: ResearchPanel's submit body silently strips both region/
  // regions (the backend 422s a non-empty one for corpus='ci') and
  // marketing_categories is sent-but-ignored server-side — so leaving
  // these rows visible let a user toggle a control that crossed the wire
  // and did nothing, with no error. (A third historical trigger, the
  // keyword "Other Documents" mode, was removed 2026-07-29.)
  const hideRegionScoped = isLetters || corpus === 'ci';
  // Approval Letters hides ONLY the Region row, not the category rows. The
  // corpus is US-only and no request carries a region (useApprovalLetterSearch
  // omits it entirely; ResearchPanel hardcodes ``regions: ['US']``), so the
  // pills changed nothing — and because ``isModeEnabled`` requires
  // ``regions === ['US']``, clicking one ran the mode through
  // ``coerceSearchMode`` and silently dropped the user back to Labels.
  // ``marketingCategories`` is the opposite case: it IS load-bearing here
  // (fda_approval_letter_routes → ``resolve_statuses_strict``), so Application
  // Type / Additional Filters must keep rendering — hence a flag separate from
  // ``hideRegionScoped`` rather than another member of it.
  const hideRegionRow = hideRegionScoped || corpus === 'approval_letters';
  const usSelected = filters.regions.includes('US');
  const MODE_OPTIONS = buildModeOptions(isAdmin, surface, filters.regions, hasLabelNegotiations)
    .map((opt) => (labelsOnly && opt.value !== 'labels'
      ? { ...opt, disabled: true, tooltip: INSTANT_LABELS_ONLY_TOOLTIP }
      : opt));
  const selectedSbaDocTypes = normalizeSbaDocTypes(filters.sbaDocTypes);
  const showSbaDocTypes =
    filters.searchMode === 'multi_review'
    && (
      surface === 'cogni'
      || (
        surface === 'investigator'
        && isUsReviewScope(filters.regions, isAdmin)
      )
    );

  const handleRegionToggle = (region: SearchRegion) => {
    const toggled = (surface === 'cogni' || surface === 'keyword' ? toggleRegionCogni : toggleRegion)(filters, region);
    // B3: after toggling a region the current searchMode may become invalid
    // (e.g. Approval Letters / Label Negotiations greyed when regions is no
    // longer exactly US, or Cogni multi_review when it is no longer US-only).
    onFiltersChange(coerceSearchMode(toggled, surface, isAdmin, hasLabelNegotiations));
  };

  const handleCategoryToggle = (key: string) => {
    const current = filters.marketingCategories;
    const next = current.includes(key)
      ? current.filter((k) => k !== key)
      : [...current, key];
    onFiltersChange({ ...filters, marketingCategories: next });
  };

  // Generic toggle for the letter-filter arrays.  Mirrors
  // ``handleCategoryToggle`` but parameterised on the field so all
  // letter rows (Type / Corpus / Role / Division) share one code path.
  const toggleLetterFilter = (
    field: 'letterTypes' | 'letterCorpus' | 'letterRoles' | 'letterDivisions',
    key: string,
  ) => {
    const current = filters[field];
    const next = current.includes(key)
      ? current.filter((k) => k !== key)
      : [...current, key];
    onFiltersChange({ ...filters, [field]: next });
  };

  // Non-US categories to show
  // De-duplicated by key and excluding keys the US pills already show, so
  // US + US_FDA never renders NDA/BLA/ANDA twice.
  const usShownKeys = new Set(
    usSelected ? [...US_MAIN_CATEGORIES, ...US_EXTRA_CATEGORIES].map((c) => c.key) : [],
  );
  const nonUsCategories = filters.regions
    .filter((r) => r !== 'US')
    .flatMap((r) => NON_US_CATEGORIES[r] ?? [])
    .filter((c, i, all) => !usShownKeys.has(c.key) && all.findIndex((o) => o.key === c.key) === i);

  // Render helper for the FDA-letter pill rows.  Used in both the
  // compact (``small=true``) and full (``small=false``) layouts.
  const letterFilterRows = (small: boolean) => (
    <>
      <LetterPillGroup
        label="Letter Type"
        options={LETTER_TYPE_OPTIONS}
        selected={filters.letterTypes}
        onToggle={(k) => toggleLetterFilter('letterTypes', k)}
        isLoading={isLoading}
        small={small}
      />
      <LetterPillGroup
        label="Letter Corpus"
        options={LETTER_CORPUS_OPTIONS}
        selected={filters.letterCorpus}
        onToggle={(k) => toggleLetterFilter('letterCorpus', k)}
        isLoading={isLoading}
        small={small}
      />
      <LetterPillGroup
        label="Division"
        options={LETTER_DIVISION_OPTIONS}
        selected={filters.letterDivisions}
        onToggle={(k) => toggleLetterFilter('letterDivisions', k)}
        isLoading={isLoading}
        small={small}
      />
      <LetterPillGroup
        label="Type"
        options={LETTER_ROLE_OPTIONS}
        selected={filters.letterRoles}
        onToggle={(k) => toggleLetterFilter('letterRoles', k)}
        isLoading={isLoading}
        small={small}
      />
    </>
  );

  // Cogni's additive "include drafts in this labels search" toggle — the third
  // Label Negotiations control. Greyed (never hidden) when the tenant owns no
  // drafts: a vanishing control reads as a broken build, a greyed one reads as
  // "nothing uploaded yet". Only the disabled state gets a Tooltip wrapper —
  // Tooltip always renders its bubble, so wrapping unconditionally would flash
  // an empty box on hover.
  const labelNegotiationsCheckbox = (small: boolean) => {
    if (!onIncludeLabelNegotiationsChange || filters.searchMode !== 'labels') return null;
    const control = (
      <label
        className={`flex items-center select-none ${small ? 'gap-1.5 text-xs' : 'mt-2 gap-2 text-xs'} ${
          hasLabelNegotiations ? 'text-slate-600 cursor-pointer' : 'text-slate-400 cursor-not-allowed'
        }`}
      >
        <input
          type="checkbox"
          checked={!!includeLabelNegotiations}
          onChange={(e) => onIncludeLabelNegotiationsChange(e.target.checked)}
          disabled={isLoading || !hasLabelNegotiations}
          className="rounded border-slate-300 text-indigo-600 focus:ring-indigo-500 disabled:opacity-50"
        />
        {small ? 'Drafts' : 'Include draft label negotiations'}
      </label>
    );
    return hasLabelNegotiations
      ? control
      : <Tooltip text={NO_LABEL_NEGOTIATIONS_TOOLTIP}>{control}</Tooltip>;
  };

  const sbaDocTypeRow = (small: boolean) => (
    <div className={small ? 'flex items-center gap-2' : ''}>
      <span
        className={
          small
            ? 'text-xs font-semibold text-slate-500'
            : 'text-sm font-semibold text-slate-700 block mb-2'
        }
      >
        {small ? 'HA Type' : 'HA Document Type'}
      </span>
      <div className="flex items-center gap-2 flex-wrap">
        {SBA_DOC_TYPE_OPTIONS.map((option) => (
          <Pill
            key={option.value}
            label={small ? option.compactLabel : option.label}
            active={selectedSbaDocTypes.includes(option.value)}
            onClick={() =>
              onFiltersChange({
                ...filters,
                sbaDocTypes: toggleSbaDocType(selectedSbaDocTypes, option.value),
              })
            }
            disabled={isLoading}
            small={small}
            title={small ? option.label : undefined}
          />
        ))}
      </div>
    </div>
  );

  // ── Inline (Keyword) layout: one flat row of labelled groups ─────────
  if (inline) {
    const groupLabel = 'text-[12px] text-ink-2';
    return (
      <div className="w-full">
        <div className="flex flex-wrap items-center gap-x-6 gap-y-3.5">
          {documentsSlot ?? (
          <div className="flex items-center gap-2 flex-wrap">
            <span className={groupLabel}>Documents</span>
            {MODE_OPTIONS.map((opt) => {
              const pill = (
                <Pill key={opt.value} label={opt.label} active={filters.searchMode === opt.value}
                  onClick={() => { if (!opt.disabled) onFiltersChange({ ...filters, searchMode: opt.value }); }}
                  disabled={isLoading || !!opt.disabled} />
              );
              return opt.disabled ? (
                <Tooltip key={opt.value} text={opt.tooltip ?? MODE_DISABLED_TOOLTIP}>{pill}</Tooltip>
              ) : pill;
            })}
          </div>
          )}
          {(showLetterFilters ?? isLetters) && letterFilterRows(true)}
          {!hideRegionRow && (
            <div className="flex items-center gap-2 flex-wrap">
              <span className={groupLabel}>Region</span>
              {keywordRegionOptions(isAdmin).map((opt) => {
                const [regionDisabled, regionTip] = regionGate(opt.value);
                const pill = (
                  <Pill key={opt.value} label={regionDisplayLabel(opt.value, usFdaVisible, opt.label)} mono soon={opt.soon}
                    active={filters.regions.includes(opt.value)}
                    onClick={() => { if (!regionDisabled) handleRegionToggle(opt.value); }}
                    disabled={isLoading || regionDisabled} />
                );
                return regionDisabled ? (
                  <Tooltip key={opt.value} text={regionTip}>{pill}</Tooltip>
                ) : pill;
              })}
            </div>
          )}
          {!hideRegionScoped && usSelected && (
            <div className="flex items-center gap-2 flex-wrap">
              <span className={groupLabel}>Application</span>
              {US_MAIN_CATEGORIES.map((opt) => (
                <Pill key={opt.key} label={opt.label} active={filters.marketingCategories.includes(opt.key)}
                  onClick={() => handleCategoryToggle(opt.key)} disabled={isLoading} />
              ))}
            </div>
          )}
          {!hideRegionScoped && (usSelected || nonUsCategories.length > 0) && (
            <button
              type="button"
              onClick={() => setShowExtra(!showExtra)}
              aria-expanded={showExtra}
              className="text-[13px] text-accent hover:text-accent-deep"
            >
              More filters
            </button>
          )}
          {!hideRegionScoped && showExtra && (
            <div className="basis-full flex items-center gap-2 flex-wrap">
              {[...(usSelected ? US_EXTRA_CATEGORIES : []), ...nonUsCategories].map((opt) => (
                <Pill key={opt.key} label={opt.label} active={filters.marketingCategories.includes(opt.key)}
                  onClick={() => handleCategoryToggle(opt.key)} disabled={isLoading} />
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  // ── Compact single-line mode ───────────────────────────────────────
  if (compact) {
    return (
      <div className="flex items-center gap-2 flex-wrap">
        {/* Document Type (always visible — even in FDA Letters mode so the
            user can switch back to Labels). */}
        <span className="text-xs font-semibold text-slate-500">Type</span>
        {MODE_OPTIONS.map((opt) => {
          const pill = (
            <Pill key={opt.value} label={opt.label} active={filters.searchMode === opt.value}
              onClick={() => { if (!opt.disabled) onFiltersChange({ ...filters, searchMode: opt.value }); }}
              disabled={isLoading || !!opt.disabled} small />
          );
          return opt.disabled ? (
            <Tooltip key={opt.value} text={opt.tooltip ?? MODE_DISABLED_TOOLTIP}>{pill}</Tooltip>
          ) : pill;
        })}
        {showSbaDocTypes && (
          <>
            <div className="h-4 w-px bg-slate-200" />
            {sbaDocTypeRow(true)}
          </>
        )}
        {labelNegotiationsCheckbox(true)}
        {isLetters && (
          <>
            <div className="h-4 w-px bg-slate-200" />
            {letterFilterRows(true)}
          </>
        )}
        {/* Region and the category pills are gated SEPARATELY: approval_letters
            drops Region but keeps the categories (see ``hideRegionRow``). Each
            block owns the divider that precedes it, so hiding one does not
            strand the other's separator. (The category block's divider still
            renders when the selection yields no category pills at all — e.g.
            regions=[] or a CA/AU/JP-only selection. That predates this split
            and is unreachable for approval_letters, whose mode gate requires
            regions === ['US'].) */}
        {!hideRegionRow && (
          <>
            <div className="h-4 w-px bg-slate-200" />
            {/* Region */}
            <span className="text-xs font-semibold text-slate-500">Region</span>
            {visibleRegionOptions(surface, isAdmin).map((opt) => {
              const [regionDisabled, regionTip] = regionGate(opt.value);
              // Browsers suppress all pointer events (including the title tooltip)
              // on disabled buttons — wrap in a span so the hover lands on the
              // non-disabled wrapper and the native title fires.
              const pill = (
                <Pill key={opt.value} label={regionDisplayLabel(opt.value, usFdaVisible, opt.label)} active={filters.regions.includes(opt.value)}
                  onClick={() => { if (!regionDisabled) handleRegionToggle(opt.value); }}
                  disabled={isLoading || regionDisabled}
                  small />
              );
              return regionDisabled ? (
                <Tooltip key={opt.value} text={regionTip}>{pill}</Tooltip>
              ) : pill;
            })}
          </>
        )}
        {!hideRegionScoped && (
          <>
            <div className="h-4 w-px bg-slate-200" />
            {/* US main categories */}
            {usSelected && (
              <>
                {US_MAIN_CATEGORIES.map((opt) => (
                  <Pill key={opt.key} label={opt.label} active={filters.marketingCategories.includes(opt.key)}
                    onClick={() => handleCategoryToggle(opt.key)} disabled={isLoading} small />
                ))}
              </>
            )}
            {/* Non-US categories */}
            {nonUsCategories.map((opt) => (
              <Pill key={opt.key} label={opt.label} active={filters.marketingCategories.includes(opt.key)}
                onClick={() => handleCategoryToggle(opt.key)} disabled={isLoading} small />
            ))}
          </>
        )}
      </div>
    );
  }

  // ── Full layout ──────────────────────────────────────────────────────
  return (
    <div className="relative w-full">
      <div className="relative bg-white border border-line rounded px-5 py-4 space-y-4">
        {/* Extra fields (e.g. Drug/Mfr for keyword search) */}
        {children && (
          <div className="flex items-center gap-4 flex-wrap pb-3 border-b border-slate-100">
            {children}
          </div>
        )}

        {/* Document Type + Region (same row).  Document Type pills are
            always rendered — even when corpus=fda_letters — so the user
            can switch back to Labels.  Region (and the divider) only
            renders for the label corpus. */}
        <div className="flex items-start gap-8 flex-wrap">
          <div>
            <label className="text-sm font-semibold text-slate-700 block mb-2">Document Type</label>
            <div className="flex items-center gap-2 flex-wrap">
              {MODE_OPTIONS.map((opt) => {
                const pill = (
                  <Pill
                    key={opt.value}
                    label={opt.label}
                    active={filters.searchMode === opt.value}
                    onClick={() => { if (!opt.disabled) onFiltersChange({ ...filters, searchMode: opt.value }); }}
                    disabled={isLoading || !!opt.disabled}
                  />
                );
                return opt.disabled ? (
                  <Tooltip key={opt.value} text={opt.tooltip ?? MODE_DISABLED_TOOLTIP}>{pill}</Tooltip>
                ) : pill;
              })}
            </div>
            {labelNegotiationsCheckbox(false)}
          </div>

          {!hideRegionRow && (
            <>
              <div className="h-10 w-px bg-slate-200 mt-5"></div>

              <div>
                <label className="text-sm font-semibold text-slate-700 block mb-2">Region</label>
                <div className="flex items-center gap-2 flex-wrap">
                  {visibleRegionOptions(surface, isAdmin).map((opt) => {
                    const [regionDisabled, regionTip] = regionGate(opt.value);
                    const pill = (
                      <Pill
                        key={opt.value}
                        label={regionDisplayLabel(opt.value, usFdaVisible, opt.label)}
                        active={filters.regions.includes(opt.value)}
                        onClick={() => { if (!regionDisabled) handleRegionToggle(opt.value); }}
                        disabled={isLoading || regionDisabled}
                      />
                    );
                    return regionDisabled ? (
                      <Tooltip key={opt.value} text={regionTip}>{pill}</Tooltip>
                    ) : pill;
                  })}
                </div>
              </div>
            </>
          )}
        </div>

        {showSbaDocTypes && (
          sbaDocTypeRow(false)
        )}

        {isLetters && (
          <div className="flex flex-col gap-4">
            {letterFilterRows(false)}
          </div>
        )}

        {!hideRegionScoped && (
          <>
            {/* Application Type (US only) */}
            {usSelected && (
              <div>
                <label className="text-sm font-semibold text-slate-700 block mb-2">Application Type</label>
                <div className="flex items-center gap-2 flex-wrap">
                  {US_MAIN_CATEGORIES.map((opt) => (
                    <Pill
                      key={opt.key}
                      label={opt.label}
                      active={filters.marketingCategories.includes(opt.key)}
                      onClick={() => handleCategoryToggle(opt.key)}
                      disabled={isLoading}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Non-US categories */}
            {nonUsCategories.length > 0 && (
              <div>
                <label className="text-sm font-semibold text-slate-700 block mb-2">Categories</label>
                <div className="flex items-center gap-2 flex-wrap">
                  {nonUsCategories.map((opt) => (
                    <Pill
                      key={opt.key}
                      label={opt.label}
                      active={filters.marketingCategories.includes(opt.key)}
                      onClick={() => handleCategoryToggle(opt.key)}
                      disabled={isLoading}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Additional Filters (US only, expandable) */}
            {usSelected && (
              <div>
                <button
                  onClick={() => setShowExtra(!showExtra)}
                  className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700 transition-colors"
                >
                  <svg
                    className={`w-4 h-4 transition-transform duration-200 ${showExtra ? 'rotate-90' : ''}`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                  Additional Filters
                </button>
                {showExtra && (
                  <div className="flex items-center gap-2 flex-wrap mt-2">
                    {US_EXTRA_CATEGORIES.map((opt) => (
                      <Pill
                        key={opt.key}
                        label={opt.label}
                        active={filters.marketingCategories.includes(opt.key)}
                        onClick={() => handleCategoryToggle(opt.key)}
                        disabled={isLoading}
                      />
                    ))}
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default SearchFiltersComponent;
